import { CircleAlert, LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { advertisingQueries, wave, waveQueries } from '@round/api';
import type { Currency } from '@/types/creator';
import { PLATFORM_ICON, PLATFORM_LABEL } from '@/utility/campaignDisplay';
import { currencyOptions, supportedCurrencies } from '@/utility/money';
import { showNotification } from '@/utility/showNotification';
import AccountAvatar from '@/creator/components/AccountAvatar';
import CurrencySelect from '@/creator/components/CurrencySelect';
import PlatformIcon from '@/creator/components/PlatformIcon';
import StatusPill from '@/creator/components/StatusPill';
import { useWaveAuth } from '@/auth/WaveAuthContext';
import AvailabilityControls from './AvailabilityControls';
import type { AvailabilityField } from './AvailabilityControls';
import BundleOffers from './BundleOffers';
import type { BundleOfferSaveResult } from './BundleOffers';

/**
 * Creator-portal Profile/Account page, ported from the prototype's ProfilePage
 * (ROU-1192). The status pill (me/), collaboration and CPM availability toggles,
 * PayPal email (me/account/, read + write), connected accounts (me/accounts/) and
 * display-currency select (me/, driven by the wallet display currency) are all
 * wired to the real API. Bundle offers (me/bundle-offers/) are listed, added,
 * edited and removed in place.
 */

const SUPPORT_EMAIL = 'hello@wave.me';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Falls back to GBP when the creator has picked no display currency.
const DEFAULT_CURRENCY: Currency = 'GBP';

const AVAILABILITY_SUCCESS: Record<AvailabilityField, { open: string; closed: string }> = {
    open_to_collaborate: {
        open: 'You’re now open to collaborate.',
        closed: 'You’re no longer shown for new campaigns.',
    },
    open_to_cpm: {
        open: 'You’re now open to CPM campaigns.',
        closed: 'You’re no longer shown for new CPM campaigns.',
    },
};

// The backend Currency.name is the portal's currency code (GBP/USD/EUR).
const isSupportedCurrency = (value: string | null): value is Currency => {
    return value !== null && supportedCurrencies.includes(value as Currency);
};

const STATUS_PILL: Record<wave.CreatorSelfStatus, string> = {
    unapproved: 'review',
    approved: 'approved',
    // Red is reserved for errors, so a suspended account stays amber, not danger.
    suspended: 'review',
};

const STATUS_LABELS: Record<wave.CreatorSelfStatus, string> = {
    unapproved: 'Pending approval',
    approved: 'Approved',
    suspended: 'Suspended',
};

const STATUS_BLURB: Record<wave.CreatorSelfStatus, string> = {
    unapproved: 'We’re reviewing your details. You can still respond to invites while we do.',
    approved: 'You’re all set. Keep your payment details current so payouts land smoothly.',
    suspended: `Your account is paused. Email ${SUPPORT_EMAIL} if you think this is a mistake.`,
};

const ProfilePage = () => {
    const { logout } = useWaveAuth();
    const selfQuery = useQuery(waveQueries.creatorSelf.options.self());
    const accountQuery = useQuery(waveQueries.creatorSelf.options.account());
    const accountsQuery = useQuery(waveQueries.creatorSelf.options.accounts());
    const bundleOffersQuery = useQuery(waveQueries.creatorBundleOffer.options.list());
    const createBundleOffer = useMutation(waveQueries.creatorBundleOffer.options.create());
    const updateBundleOffer = useMutation(waveQueries.creatorBundleOffer.options.update());
    const removeBundleOffer = useMutation(waveQueries.creatorBundleOffer.options.remove());
    const currenciesQuery = useQuery(advertisingQueries.currency.options.list({}));

    const savedEmail = accountQuery.data?.paypal_email ?? '';
    const [email, setEmail] = useState(savedEmail);
    const [emailError, setEmailError] = useState('');
    const [isSavingEmail, setIsSavingEmail] = useState(false);
    const [updatingAvailability, setUpdatingAvailability] = useState<AvailabilityField | null>(null);
    const [isSavingCurrency, setIsSavingCurrency] = useState(false);

    // The select is driven by me/'s display currency (name is the code); GBP until set.
    const savedCurrencyName = selfQuery.data?.display_currency?.name ?? null;
    const displayCurrency: Currency = isSupportedCurrency(savedCurrencyName) ? savedCurrencyName : DEFAULT_CURRENCY;

    const emailErrorId = 'paypal-email-error';

    useEffect(() => {
        setEmail(savedEmail);
        setEmailError('');
    }, [savedEmail]);

    const status = selfQuery.data?.creator_status ?? null;

    const connectedAccounts = accountsQuery.data ?? [];
    const connectedPlatforms = [...new Set(connectedAccounts.map((connectedAccount) => connectedAccount.platform))];

    const handleCurrencyChange = async (currency: Currency) => {
        if (isSavingCurrency || currency === displayCurrency) {
            return;
        }
        const target = (currenciesQuery.data ?? []).find((option) => option.name === currency);
        if (!target) {
            showNotification('Couldn’t update your currency. Try again shortly.', 'error');
            return;
        }
        setIsSavingCurrency(true);
        try {
            const response = await wave.patchCreatorAccount({ display_currency: target.id });
            if (response.status === 200) {
                await Promise.all([selfQuery.refetch(), accountQuery.refetch()]);
                showNotification(`All amounts are now shown in ${currency}.`, 'success');
                return;
            }
            showNotification('Couldn’t update your currency. Try again shortly.', 'error');
        } catch {
            showNotification(`Something went wrong. If it persists, email ${SUPPORT_EMAIL}.`, 'error');
        } finally {
            setIsSavingCurrency(false);
        }
    };

    const handleAvailabilityChange = async (field: AvailabilityField, nextOpen: boolean) => {
        if (updatingAvailability !== null) {
            return;
        }
        setUpdatingAvailability(field);
        try {
            const response = await wave.patchCreatorAccount(
                field === 'open_to_collaborate' ? { open_to_collaborate: nextOpen } : { open_to_cpm: nextOpen }
            );
            if (response.status === 200) {
                await accountQuery.refetch();
                showNotification(AVAILABILITY_SUCCESS[field][nextOpen ? 'open' : 'closed'], 'success');
                return;
            }
            showNotification('Couldn’t update your availability. Try again shortly.', 'error');
        } catch {
            showNotification(`Something went wrong. If it persists, email ${SUPPORT_EMAIL}.`, 'error');
        } finally {
            setUpdatingAvailability(null);
        }
    };

    const handleBundleOfferSave = async (
        offerId: number | null,
        body: wave.CreatorBundleOfferBody
    ): Promise<BundleOfferSaveResult> => {
        try {
            if (offerId === null) {
                await createBundleOffer.mutateAsync(body);
            } else {
                await updateBundleOffer.mutateAsync({ id: offerId, ...body });
            }
            await bundleOffersQuery.refetch();
            showNotification(
                offerId === null ? 'Your bundle has been added.' : 'Your bundle is up to date.',
                'success'
            );
            return { status: 'saved' };
        } catch (error) {
            const isExpected =
                offerId === null
                    ? waveQueries.creatorBundleOffer.utils.create.isExpectedErrorResponse
                    : waveQueries.creatorBundleOffer.utils.update.isExpectedErrorResponse;
            if (!(error instanceof Error) || !isExpected(error)) {
                showNotification(`Something went wrong. If it persists, email ${SUPPORT_EMAIL}.`, 'error');
                return { status: 'failed' };
            }
            const { response } = error;
            if (response.status !== 400) {
                showNotification('Couldn’t save your bundle. Try again shortly.', 'error');
                return { status: 'failed' };
            }
            if (Array.isArray(response.data)) {
                return { status: 'invalid', errors: { non_field_errors: response.data[0] } };
            }
            return {
                status: 'invalid',
                errors: Object.fromEntries(
                    Object.entries(response.data).map(([field, fieldError]) => [
                        field,
                        Array.isArray(fieldError) ? fieldError[0] : fieldError,
                    ])
                ),
            };
        }
    };

    const handleBundleOfferDelete = async (offerId: number): Promise<boolean> => {
        try {
            await removeBundleOffer.mutateAsync(offerId);
            await bundleOffersQuery.refetch();
            showNotification('Your bundle has been removed.', 'success');
            return true;
        } catch (error) {
            if (error instanceof Error && waveQueries.creatorBundleOffer.utils.remove.isExpectedErrorResponse(error)) {
                showNotification('Couldn’t remove your bundle. Try again shortly.', 'error');
            } else {
                showNotification(`Something went wrong. If it persists, email ${SUPPORT_EMAIL}.`, 'error');
            }
            return false;
        }
    };

    const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const trimmed = email.trim();
        if (!EMAIL_PATTERN.test(trimmed)) {
            setEmailError('Enter a valid email address.');
            return;
        }
        setIsSavingEmail(true);
        try {
            const response = await wave.patchCreatorAccount({ paypal_email: trimmed });
            if (response.status === 200) {
                await accountQuery.refetch();
                showNotification('Your payment details are up to date.', 'success');
                return;
            }
            const error = response.data.paypal_email;
            const message = (Array.isArray(error) ? error[0] : error) ?? 'Enter a valid PayPal email.';
            setEmailError(message);
        } catch {
            showNotification(`Something went wrong. If it persists, email ${SUPPORT_EMAIL}.`, 'error');
        } finally {
            setIsSavingEmail(false);
        }
    };

    return (
        <div className="page account-page">
            <header className="page-heading">
                <div>
                    <h1>Account</h1>
                    <p>Your status and where we send your payments.</p>
                </div>
            </header>

            <section className="account-card status-card">
                {selfQuery.isLoading ? (
                    <p>Loading your status…</p>
                ) : selfQuery.isError || !status ? (
                    <p>Couldn’t load your status. Try again shortly.</p>
                ) : (
                    <>
                        <div>
                            <h2>Your status</h2>
                            <p>{STATUS_BLURB[status]}</p>
                        </div>
                        <StatusPill status={STATUS_PILL[status]}>{STATUS_LABELS[status]}</StatusPill>
                    </>
                )}
            </section>

            <section className="account-card availability-card">
                <div>
                    <h2>Availability</h2>
                    <p>
                        Choose which new campaigns we can show you for. Changes will not affect campaigns you are
                        already on.
                    </p>
                    {accountQuery.isLoading ? (
                        <p>Loading your availability…</p>
                    ) : accountQuery.isError || !accountQuery.data ? (
                        <p>Couldn’t load your availability. Try again shortly.</p>
                    ) : (
                        <AvailabilityControls
                            openToCollaborate={accountQuery.data.open_to_collaborate}
                            openToCpm={accountQuery.data.open_to_cpm}
                            disabled={updatingAvailability !== null}
                            onChange={handleAvailabilityChange}
                        />
                    )}
                </div>
            </section>

            <section className="account-card social-accounts-card">
                <div className="social-accounts-card__heading">
                    <div>
                        <h2>Connected accounts</h2>
                        <p>The Instagram and TikTok accounts linked to your profile.</p>
                    </div>
                    {accountsQuery.data ? (
                        <StatusPill status="approved">{connectedAccounts.length} connected</StatusPill>
                    ) : null}
                </div>
                {accountsQuery.isLoading ? (
                    <p>Loading your connected accounts…</p>
                ) : accountsQuery.isError ? (
                    <p>Couldn’t load your connected accounts. Try again shortly.</p>
                ) : connectedAccounts.length === 0 ? (
                    <p>No accounts are linked to your profile yet.</p>
                ) : (
                    <div className="social-account-groups">
                        {connectedPlatforms.map((platform) => {
                            const platformAccounts = connectedAccounts.filter(
                                (connectedAccount) => connectedAccount.platform === platform
                            );
                            const displayPlatform = PLATFORM_ICON[platform];
                            return (
                                <div className="social-account-group" key={platform}>
                                    <div className="social-account-group__heading">
                                        <span
                                            className={`platform-logo platform-logo--${displayPlatform.toLowerCase()}`}
                                        >
                                            <PlatformIcon platform={displayPlatform} size={15} />
                                        </span>
                                        <h3>{PLATFORM_LABEL[platform]}</h3>
                                        <small>{platformAccounts.length}</small>
                                    </div>
                                    <div className="social-account-list">
                                        {platformAccounts.map((connectedAccount) => (
                                            <span className="social-account" key={connectedAccount.id}>
                                                <AccountAvatar src={connectedAccount.avatar_url} />
                                                <b>{connectedAccount.handle}</b>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            <section className="account-card">
                <h2>Bundle offers</h2>
                <p>Package deals you can offer on campaigns, like three posts for one set price.</p>
                {bundleOffersQuery.isLoading || accountsQuery.isLoading || currenciesQuery.isLoading ? (
                    <p>Loading your bundle offers…</p>
                ) : bundleOffersQuery.isError || accountsQuery.isError || currenciesQuery.isError ? (
                    <p>Couldn’t load your bundle offers. Try again shortly.</p>
                ) : (
                    <BundleOffers
                        offers={bundleOffersQuery.data ?? []}
                        accounts={connectedAccounts}
                        currencies={currenciesQuery.data ?? []}
                        defaultCurrency={displayCurrency}
                        onSave={handleBundleOfferSave}
                        onDelete={handleBundleOfferDelete}
                    />
                )}
            </section>

            <section className="account-card">
                <h2>Payment details</h2>
                <p>Choose how amounts appear and where approved payouts are sent.</p>
                {accountQuery.isLoading ? (
                    <p>Loading your payment details…</p>
                ) : accountQuery.isError || !accountQuery.data ? (
                    <p>Couldn’t load your payment details. Try again shortly.</p>
                ) : (
                    <form onSubmit={handleEmailSubmit} noValidate>
                        <div className="field">
                            <span id="currency-field-label">Currency</span>
                            <CurrencySelect
                                labelId="currency-field-label"
                                value={displayCurrency}
                                options={currencyOptions}
                                onChange={handleCurrencyChange}
                                disabled={isSavingCurrency}
                            />
                        </div>
                        <label className={emailError ? 'field field--error' : 'field'}>
                            <span>PayPal email</span>
                            <div>
                                <input
                                    aria-label="PayPal email"
                                    aria-invalid={Boolean(emailError)}
                                    aria-describedby={emailError ? emailErrorId : undefined}
                                    inputMode="email"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    value={email}
                                    onChange={(event) => {
                                        setEmail(event.target.value);
                                        setEmailError('');
                                    }}
                                />
                            </div>
                            {emailError && (
                                <small id={emailErrorId} role="alert">
                                    <CircleAlert size={13} /> <span>{emailError}</span>
                                </small>
                            )}
                        </label>
                        <button
                            className="button button--primary"
                            type="submit"
                            disabled={email.trim() === savedEmail || isSavingEmail}
                        >
                            {isSavingEmail ? 'Saving…' : 'Save changes'}
                        </button>
                    </form>
                )}
            </section>

            <section className="account-card account-session-card">
                <div>
                    <h2>Session</h2>
                    <p>Log out of this device and return to the public Wave page.</p>
                </div>
                <button className="button button--secondary" type="button" onClick={logout}>
                    <LogOut size={15} /> <span>Log out</span>
                </button>
            </section>
        </div>
    );
};

export default ProfilePage;
